import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Mail, UserCheck, Loader2, Download, ExternalLink, AlertCircle, Building2, Clock } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { invitationService, InviteCheckResult } from '../services/invitationService';
import { emailApiService, EmailAccount } from '../../../../features/scopingAi/services/email/emailApiService';
import { usePermissions } from '../contextapi/PermissionsContext';

type Step = 1 | 2 | 3;

const STEPS = [
    { id: 1 as Step, label: 'Accept Invite',   desc: 'Join your organisation', icon: UserCheck },
    { id: 2 as Step, label: 'Connect Gmail',   desc: 'Link your email',         icon: Mail },
    { id: 3 as Step, label: 'Email Signature', desc: 'Import your signature',   icon: Download },
];

export const Onboarding: React.FC = () => {
    const navigate = useNavigate();
    const { refreshPermissions } = usePermissions();

    const [step, setStep] = useState<Step>(1);
    const [initLoading, setInitLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [inviteInfo, setInviteInfo] = useState<Extract<InviteCheckResult, { hasInvite: true }> | null>(null);
    const [accepting, setAccepting] = useState(false);

    const [accounts, setAccounts] = useState<EmailAccount[]>([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [connectingGmail, setConnectingGmail] = useState(false);

    const [importingSignature, setImportingSignature] = useState(false);
    const [signatureImported, setSignatureImported] = useState(false);

    useEffect(() => {
        const init = async () => {
            setInitLoading(true);
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) { navigate('/login'); return; }

                const { data: profile } = await supabase
                    .from('base_profiles').select('is_onboarded').eq('id', user.id).single();
                if (profile?.is_onboarded) { navigate('/tender'); return; }

                // Invitation check is the ONLY way to proceed past Step 1.
                // No invite = blocked on Step 1 (disabled button, "contact admin" message).
                // Note: the DB trigger adds new users as 'member' automatically, but we
                // deliberately ignore that here — only an accepted invitation grants access.
                if (user.email) {
                    const result = await invitationService.checkEmail(user.email);
                    if (result.hasInvite) {
                        setInviteInfo(result);
                    }
                    // If no invite, stay on Step 1 — no fallthrough to Step 2
                }
            } catch (err) {
                console.error('[onboarding] init error:', err);
            } finally {
                setInitLoading(false);
            }
        };
        init();
    }, [navigate]);

    useEffect(() => {
        if (step !== 2) return;
        setLoadingAccounts(true);
        emailApiService.getMyAccounts()
            .then(setAccounts).catch(console.error)
            .finally(() => setLoadingAccounts(false));
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') window.history.replaceState({}, '', window.location.pathname);
    }, [step]);

    const handleAccept = async () => {
        setError(null);
        setAccepting(true);
        try {
            if (inviteInfo) await invitationService.acceptInvitation(inviteInfo.invitationId);
            await refreshPermissions();
            setStep(2);
        } catch (err: unknown) {
            setError((err as any)?.response?.data?.error || 'Failed to accept invitation');
        } finally {
            setAccepting(false);
        }
    };

    const handleConnectGmail = async () => {
        setConnectingGmail(true);
        try {
            const { url } = await emailApiService.connectAccount(undefined, '/onboarding');
            window.location.href = url;
        } catch {
            setError('Failed to start Gmail connection');
            setConnectingGmail(false);
        }
    };

    const handleImportSignature = async (accountId: string) => {
        setImportingSignature(true);
        setError(null);
        try {
            await emailApiService.getGmailSignature(accountId);
            setSignatureImported(true);
        } catch {
            setError('Failed to import signature');
        } finally {
            setImportingSignature(false);
        }
    };

    const handleFinish = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await supabase.from('base_profiles').update({ is_onboarded: true }).eq('id', user.id);
        navigate('/tender');
    };

    if (initLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground">Setting up your account...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex overflow-hidden">

            {/* ── Content ── */}
            <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#f8fafc' }}>

                {/* Mobile step bar */}
                <div className="md:hidden flex border-b bg-white">
                    {STEPS.map((s) => {
                        const done = step > s.id;
                        const active = step === s.id;
                        return (
                            <div key={s.id} className={`flex-1 flex flex-col items-center py-3 gap-1 relative text-xs font-medium transition-colors ${
                                active ? 'text-primary' : done ? 'text-emerald-600' : 'text-gray-300'
                            }`}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                    done ? 'bg-emerald-100' : active ? 'bg-primary/10' : 'bg-gray-100'
                                }`}>
                                    {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <s.icon className="w-3.5 h-3.5" />}
                                </div>
                                <span className="hidden sm:block">{s.label}</span>
                                {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                            </div>
                        );
                    })}
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="min-h-full flex flex-col justify-center items-center p-8 lg:p-16">
                        <div className="w-full max-w-lg">

                            {error && (
                                <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm flex gap-3 items-start">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* ── Step 1 ── */}
                            {step === 1 && (
                                <div className="space-y-6">
                                    {inviteInfo ? (
                                        <>
                                            <div>
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
                                                    style={{ background: 'rgba(55,144,197,0.08)', border: '1px solid rgba(55,144,197,0.2)', color: '#3790C5' }}>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                                    Invitation Ready
                                                </div>
                                                <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-3">
                                                    Join {inviteInfo.orgName}
                                                </h2>
                                                <p className="text-gray-500 text-base leading-relaxed">
                                                    You've been invited to join the team. Accept to get started.
                                                </p>
                                            </div>

                                            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                                <div className="p-6 flex items-start gap-4">
                                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                                                        style={{ background: 'linear-gradient(135deg, rgba(55,144,197,0.15), rgba(55,144,197,0.08))' }}>
                                                        <Building2 className="w-6 h-6 text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-bold text-gray-900 text-lg leading-tight">{inviteInfo.orgName}</p>
                                                        <p className="text-gray-400 text-sm mt-0.5">Organisation</p>
                                                    </div>
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize"
                                                        style={{ background: 'linear-gradient(135deg,#dbeafe,#eff6ff)', color: '#1d4ed8' }}>
                                                        {inviteInfo.role}
                                                    </span>
                                                </div>
                                                <div className="px-6 py-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400"
                                                    style={{ background: '#fafafa' }}>
                                                    <Clock className="w-3.5 h-3.5" />
                                                    Invitation pending acceptance
                                                </div>
                                            </div>

                                            <button
                                                onClick={handleAccept}
                                                disabled={accepting}
                                                className="w-full py-4 text-base font-semibold rounded-2xl text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transition-all hover:opacity-90 active:scale-[0.99]"
                                                style={{ background: 'linear-gradient(135deg,#3790C5,#2a6fa0)', boxShadow: '0 8px 24px rgba(55,144,197,0.3)' }}
                                            >
                                                {accepting && <Loader2 className="w-5 h-5 animate-spin" />}
                                                {accepting ? 'Accepting...' : 'Accept & Continue'}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-3">No invitation found</h2>
                                                <p className="text-gray-500 text-base leading-relaxed">
                                                    Your account exists but you don't have a pending invitation to any organisation.
                                                </p>
                                            </div>
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 flex gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                                    <AlertCircle className="w-5 h-5 text-amber-600" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-amber-800 text-sm mb-1">Invitation required</p>
                                                    <p className="text-amber-700 text-sm leading-relaxed">
                                                        Ask your organisation admin to send you an invitation to proceed.
                                                    </p>
                                                </div>
                                            </div>
                                            <button disabled
                                                className="w-full py-4 text-base font-semibold rounded-2xl text-white opacity-30 cursor-not-allowed"
                                                style={{ background: 'linear-gradient(135deg,#3790C5,#2a6fa0)' }}>
                                                Accept & Continue
                                            </button>

                                            <button
                                                onClick={async () => {
                                                    await supabase.auth.signOut();
                                                    navigate('/login');
                                                }}
                                                className="w-full py-3 text-sm font-medium rounded-2xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-center gap-2"
                                            >
                                                Try with a different account
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── Step 2 ── */}
                            {step === 2 && (
                                <div className="space-y-6">
                                    <div>
                                        <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-5">
                                            <Mail className="w-7 h-7 text-red-500" />
                                        </div>
                                        <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-3">Connect Gmail</h2>
                                        <p className="text-gray-500 text-base leading-relaxed">
                                            Link your Gmail to send and receive tender emails directly from the app.
                                        </p>
                                    </div>

                                    <div className="grid gap-3">
                                        {[
                                            { icon: '⚡', title: 'Send emails instantly', desc: 'Reach retailers without leaving the platform' },
                                            { icon: '🔄', title: 'Auto-sync replies',    desc: 'All responses appear in your tender timeline' },
                                            { icon: '📎', title: 'Attachments included', desc: 'Documents sent and received automatically' },
                                        ].map(b => (
                                            <div key={b.title} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                                                <span className="text-xl leading-none mt-0.5">{b.icon}</span>
                                                <div>
                                                    <p className="font-semibold text-sm text-gray-800">{b.title}</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">{b.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {loadingAccounts ? (
                                        <div className="flex items-center justify-center py-6 gap-3 text-gray-400">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span className="text-sm">Checking connected accounts...</span>
                                        </div>
                                    ) : accounts.length > 0 ? (
                                        <div className="space-y-3">
                                            {accounts.map(acc => (
                                                <div key={acc.id} className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
                                                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                                                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-sm text-emerald-900">{acc.email}</p>
                                                        <p className="text-xs text-emerald-600">Connected successfully</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleConnectGmail}
                                            disabled={connectingGmail}
                                            className="w-full py-4 bg-white border-2 border-gray-200 rounded-2xl font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all shadow-sm"
                                        >
                                            {connectingGmail ? (
                                                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                            ) : (
                                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                                </svg>
                                            )}
                                            {connectingGmail ? 'Redirecting to Google...' : 'Connect with Google'}
                                        </button>
                                    )}

                                    <div className="flex gap-3">
                                        <button onClick={() => setStep(3)}
                                            className="flex-1 py-3.5 text-sm font-medium text-gray-400 hover:text-gray-600 border border-gray-200 rounded-2xl hover:bg-white transition-all">
                                            Skip for now
                                        </button>
                                        {accounts.length > 0 && (
                                            <button onClick={() => setStep(3)}
                                                className="flex-1 py-3.5 font-semibold rounded-2xl text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                                                style={{ background: 'linear-gradient(135deg,#3790C5,#2a6fa0)', boxShadow: '0 8px 24px rgba(55,144,197,0.3)' }}>
                                                Continue
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Step 3 ── */}
                            {step === 3 && (
                                <div className="space-y-6">
                                    <div>
                                        <div className="w-14 h-14 rounded-2xl bg-violet-50 border border-violet-100 flex items-center justify-center mb-5">
                                            <Download className="w-7 h-7 text-violet-500" />
                                        </div>
                                        <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-3">Email Signature</h2>
                                        <p className="text-gray-500 text-base leading-relaxed">
                                            Import your Gmail signature so it appears automatically on all tender emails.
                                        </p>
                                    </div>

                                    {/* Preview mock */}
                                    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                                        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" style={{ background: '#fafafa' }}>
                                            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                            <span className="text-xs text-gray-400 ml-1">Email Preview</span>
                                        </div>
                                        <div className="p-5">
                                            <div className="text-sm text-gray-500 leading-relaxed mb-4">
                                                <p>Hi there,</p><br />
                                                <p>Please find attached the tender documentation for your review...</p>
                                            </div>
                                            <div className="border-t border-gray-100 pt-4">
                                                {signatureImported ? (
                                                    <div className="space-y-1">
                                                        <p className="text-sm font-semibold text-gray-800">Your Name</p>
                                                        <p className="text-xs text-gray-400">Your Title · Your Company</p>
                                                        <p className="text-xs text-primary">your@email.com</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <div className="h-3 bg-gray-100 rounded-full w-32 animate-pulse" />
                                                        <div className="h-2.5 bg-gray-100 rounded-full w-48 animate-pulse" />
                                                        <div className="h-2.5 bg-gray-100 rounded-full w-28 animate-pulse" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {accounts.length === 0 ? (
                                        <div className="p-5 rounded-2xl bg-amber-50 border border-amber-100 text-amber-700 text-sm flex gap-3 items-start">
                                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>No Gmail connected — import your signature later from Profile settings.</span>
                                        </div>
                                    ) : signatureImported ? (
                                        <div className="flex items-center gap-4 p-5 rounded-2xl bg-emerald-50 border border-emerald-200">
                                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-emerald-800 text-sm">Signature imported!</p>
                                                <p className="text-xs text-emerald-600 mt-0.5">Will appear automatically on all tender emails.</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {accounts.map(acc => (
                                                <button key={acc.id} onClick={() => handleImportSignature(acc.id)}
                                                    disabled={importingSignature}
                                                    className="w-full flex items-center gap-4 p-4 rounded-2xl border border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/50 disabled:opacity-60 disabled:cursor-not-allowed text-left transition-all group">
                                                    <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-100 transition-colors">
                                                        {importingSignature
                                                            ? <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                                                            : <Download className="w-5 h-5 text-violet-500" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-sm text-gray-800">{acc.email}</p>
                                                        <p className="text-xs text-gray-400 mt-0.5">
                                                            {importingSignature ? 'Importing signature...' : 'Click to import Gmail signature'}
                                                        </p>
                                                    </div>
                                                    {!importingSignature && <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-violet-400 transition-colors flex-shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button onClick={handleFinish}
                                            className="flex-1 py-3.5 text-sm font-medium text-gray-400 hover:text-gray-600 border border-gray-200 rounded-2xl hover:bg-white transition-all">
                                            Skip &amp; Enter App
                                        </button>
                                        <button onClick={handleFinish}
                                            className="flex-1 py-3.5 font-semibold rounded-2xl text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                                            style={{ background: 'linear-gradient(135deg,#3790C5,#2a6fa0)', boxShadow: '0 8px 24px rgba(55,144,197,0.3)' }}>
                                            <CheckCircle2 className="w-4 h-4" />
                                            Finish &amp; Enter App
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Onboarding;
